import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import MessageBus from '@restore/messagebus';
import { Redirect, useParams, useHistory } from 'react-router-dom';
import Auth from '../../services/Auth';
import Tournament from '../../services/Tournament';
import AuthService from '../../../services/auth/authService';
import { getTourViewed, setTourViewed } from '../../services/TourService';
import KeycloakTimeoutHandler from '../KeycloakTimeoutHandler';

const Iframe = styled.iframe`
  position: relative;
  width: calc(100% + 70px);
  height: var(--frameHeight);
  margin: -48px -35px -32px;
`;

const Wellness: FC = () => {
  const [activityID, setActivityID] = useState<number>(-1);
  const [activityType, setActivityType] = useState<string>('');
  const [groupId, setGroupId] = useState(-1);
  const updateFrameHeight = (height: number) => {
    document.documentElement.style.setProperty('--frameHeight', `${height}px`);
  };

  const history = useHistory();

  const throttling = useRef(false);

  const token = AuthService.getAuthToken();
  const { path } = useParams<{ path: string }>();

  interface ActivityLaunchData {
    activityID: number;
    slug: string;
    groupId: number;
    restorativeProgramId?: number;
  }

  type TourData = {
    tourName: string;
  };

  interface GetTourRequest {
    requestID: number;
    data: TourData;
  }

  useEffect(() => {
    const messageBus = new MessageBus();

    messageBus.subscribe('wellnessIframeHeight', (height) => {
      updateFrameHeight(height as number);
    });

    messageBus.subscribe('keepAlive', () => {
      if (throttling.current) {
        return;
      }

      throttling.current = true;
      Auth.keepAlive().then((response) => {
        if (response) {
          messageBus.publish('tokenResponse', response.new_auth);
        }
      });

      setTimeout(() => {
        throttling.current = false;
      }, 15000);
    });

    messageBus.subscribe('requestToken', () => {
      messageBus.publish('tokenResponse', token);
    });

    //Not sure why data cannot be ActivityLaunchData
    messageBus.subscribe('wellnessLaunchCboard', (data) => {
      const activityData = data as ActivityLaunchData;
      if (activityData.restorativeProgramId) {
        history.push('/communication-board/' + activityData.restorativeProgramId);
      } else {
        history.push('/communication-board/');
      }
    });

    messageBus.subscribe('wellnessActivityStart', async (data) => {
      const activityData = data as ActivityLaunchData;
      await setGroupId(activityData.groupId);
      await setActivityID(activityData.activityID);
      await setActivityType(activityData.slug);
    });

    messageBus.subscribe('openTournamentDialog', () => {
      const wellnesTournamentPathPrefix = '/play/wellness/tournament/';
      Tournament.showTournamentDialog(wellnesTournamentPathPrefix);
    });

    messageBus.subscribe('getTourViewed', (payload) => {
      messageBus.respond(
        'getTourViewed',
        (payload as GetTourRequest).requestID,
        getTourViewed((payload as GetTourRequest).data.tourName)
      );
    });

    messageBus.subscribe('setTourViewed', (tourName) => {
      setTourViewed(tourName as string);
    });
  }, []);

  let activityPathname = '';
  if (activityID !== -1 && activityType !== '') {
    activityPathname = `/play/wellness/${activityType}/${activityID}`;

    if (groupId !== -1) {
      activityPathname += `/${groupId}`;
    }
  }

  return (
    <div>
      <KeycloakTimeoutHandler />
      {activityPathname !== '' && (
        <Redirect
          push
          to={{
            pathname: activityPathname,
            state: { from: location.pathname },
          }}
        />
      )}
      <Iframe
        allowFullScreen
        allow="camera;"
        id="wellness-iframe"
        src={`${import.meta.env.VITE_WELLNESS_UI_URL}/dashboard/${path}`}
      />
    </div>
  );
};

export default Wellness;
